import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { DG } from '@cotera/sdk/core';
import { ExpressionNodeView } from './view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import type { SlashCommand } from '@cotera/client/app/components/app/rte/types/extension-interface';

export interface ExpressionOptions extends ExtensionMenuConfig {
  HTMLAttributes: Record<string, any>;
  parser?: DG.Graph.RelativeParser;
  datasetName?: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    expression: {
      /**
       * Insert an expression editor block
       */
      setExpression: (attributes?: { eraql?: string }) => ReturnType;
    };
  }
}

export const ExpressionNode = Node.create<ExpressionOptions>({
  name: 'expression',

  addOptions() {
    return {
      HTMLAttributes: {},
      parser: undefined,
      datasetName: undefined,
    };
  },

  group: 'block',

  atom: true,
  selectable: false,

  addAttributes() {
    return {
      eraql: {
        default: '',
        parseHTML: (element) => {
          const data = element.getAttribute('data-eraql');
          return data || '';
        },
        renderHTML: (attributes) => {
          return {
            'data-eraql': attributes['eraql'] || '',
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="expression"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'expression' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ExpressionNodeView);
  },

  addCommands() {
    return {
      setExpression:
        (attributes = {}) =>
        ({ commands }) => {
          // Insert the node and then move cursor after it to avoid selection highlighting
          commands.insertContent(
            {
              type: this.name,
              attrs: attributes,
            },
            {
              updateSelection: false, // <-- This prevents auto-selection
            }
          );

          // Move cursor to after the node to prevent it from being selected
          return commands.setTextSelection(-1);
        },
    };
  },

  // Add turn-into and slash command configurations
  addStorage() {
    return {
      getSlashConfig: (): SlashCommand => ({
        title: 'Expression',
        subtext: 'Insert an EraQL expression editor',
        keywords: ['expression', 'eraql', 'expr', 'formula', 'code', 'query'],
        badge: 'code-block',
        group: 'Insert',
        available: (editor) => {
          // Basic checks
          if (!editor.isEditable || !editor.can().setExpression()) {
            return false;
          }

          return true;
        },
        action: ({ editor }) => {
          editor.chain().focus().setExpression().run();
        },
      }),
    };
  },
});
